import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "pt-6"
}
const _hoisted_2 = { class: "loading-state" }
const _hoisted_3 = { class: "details-stack" }
const _hoisted_4 = { class: "detail-field" }
const _hoisted_5 = { class: "detail-field" }
const _hoisted_6 = { class: "detail-field" }
const _hoisted_7 = { class: "detail-field" }
const _hoisted_8 = { class: "detail-field" }
const _hoisted_9 = { class: "px-2" }
const _hoisted_10 = {
  class: "d-flex flex-wrap bg-white px-2 py-4",
  style: {"border-radius":"12px"}
}
const _hoisted_11 = {
  key: 0,
  class: "mt-1 ml-4",
  style: {"color":"#dc2626 !important","font-size":"13px"}
}
const _hoisted_12 = { class: "mt-8" }

import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppFieldLabel from "@/shared/components/ui/AppFieldLabel.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import { computed, onMounted, ref, watch } from "vue";
import { fetchRoles, normalizeRoles } from "@/shared/services/roles.api";
import {
  handleSubmitStaff,
  type StaffPayload,
  fetchStaffDetails,
} from "@/modules/vendor/services/vendors.api";
import { useRouter, useRoute } from "vue-router";
import AppMobileField from "@/shared/components/ui/AppMobileField.vue";

/* keep only what's used by the template (form + subtitle counter) */
type RoleItem = { id: number | string; label: string; role_name?: string | null };

export default /*@__PURE__*/_defineComponent({
  __name: 'StaffFormPage',
  setup(__props) {


const staffCount = ref(0);
const staffsSubtitle = computed(() => {
    const count = staffCount.value;
    return count === 1 ? "1 staff member" : `${count} staff members`;
});

const router = useRouter();
const route = useRoute();

/* form state */
const showForm = ref(false);
const isSubmitting = ref(false);
const firstName = ref("");
const lastName = ref("");
const email = ref("");
const phone = ref("");
const role = ref<Array<number | string>>([]);

// editing state: when `id` present in route query/params, load details
const staffId = ref<string | null>(null);
const isLoadingStaff = ref(false);
const loadStaffError = ref<string | null>(null);

const rolesLoading = ref(false);
const roleItems = ref<RoleItem[]>([]);

const firstNameError = ref("");
const lastNameError = ref("");
const emailError = ref("");
const phoneError = ref("");
const roleError = ref("");
const showValidationErrors = ref(false);

const required = (value: unknown) => Boolean(String(value ?? "").trim()) || "Required";
const emailRule = (value: unknown) => /.+@.+\..+/.test(String(value ?? "").trim()) || "Enter a valid email";
const phoneRule = (value: unknown) => Boolean(String(value ?? "").trim()) || "Enter a valid phone number";

function openForm() {
    showForm.value = true;
    resetForm();
}

function closeForm() {
    showForm.value = false;
    resetForm();
}

function resetForm() {
    firstName.value = "";
    lastName.value = "";
    email.value = "";
    phone.value = "";
    role.value = [];
    firstNameError.value = "";
    lastNameError.value = "";
    emailError.value = "";
    phoneError.value = "";
    roleError.value = "";
    showValidationErrors.value = false;
}

function validate() {
    firstNameError.value = "";
    lastNameError.value = "";
    emailError.value = "";
    phoneError.value = "";
    roleError.value = "";

    if (!firstName.value.trim()) firstNameError.value = "First name is required";
    if (!lastName.value.trim()) lastNameError.value = "Last name is required";
    if (!email.value.trim()) emailError.value = "Email is required";
    else if (!/.+@.+\..+/.test(email.value)) emailError.value = "Enter a valid email";
    if (!phone.value.trim()) phoneError.value = "Phone is required";
    if (!Array.isArray(role.value) || role.value.length === 0) {
        roleError.value = "Role is required";
    }

    return !firstNameError.value && !lastNameError.value && !emailError.value && !phoneError.value && !roleError.value;
}

function syncValidation() {
    if (!showValidationErrors.value) return;
    validate();
}

function onRoleToggle(value: RoleItem["id"], checked: boolean) {
    const idx = role.value.findIndex((v) => v === value);
    if (checked) {
        if (idx === -1) role.value.push(value);
        return;
    }
    if (idx !== -1) role.value.splice(idx, 1);
}

function normalizeRoleName(value: unknown) {
  return String(value ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

function getSelectedRoleIds(
  selectedRoles: Array<{ id?: number | string; label?: string; role_name?: string | null }>,
  availableRoles: RoleItem[],
) {
  const selectedNames = new Set(
    selectedRoles
      .map((roleItem) => normalizeRoleName(roleItem.role_name || roleItem.label))
      .filter(Boolean),
  );

  return availableRoles
    .filter((roleItem) => selectedNames.has(normalizeRoleName(roleItem.role_name || roleItem.label)))
    .map((roleItem) => roleItem.id);
}

watch([firstName, lastName, email, phone, role], syncValidation, { deep: true });

onMounted(async () => {
	    // if an id is present in query or params, fetch staff details and populate form for editing
	    const idFromQuery = String(route.query.id || route.params.id || "").trim();
	    if (!idFromQuery) {
	        rolesLoading.value = true;
	        try {
	            const payload = await fetchRoles();
	            roleItems.value = normalizeRoles(payload);
	        } catch (err) {
	            // eslint-disable-next-line no-console
	            console.error("Roles fetch failed", err);
	        } finally {
            rolesLoading.value = false;
        }
        return;
    }

    staffId.value = idFromQuery;
    isLoadingStaff.value = true;
	    rolesLoading.value = true;
	    loadStaffError.value = null;
	    try {
	        const staffPayload = await fetchStaffDetails(idFromQuery);
	        const s = (staffPayload && (staffPayload as any).data) || {};
	        const client = (s && (s as any).client) || {};

	        firstName.value = String(client.fname || "").trim();
	        lastName.value = String(client.lname || "").trim();
	        email.value = String(client.email || "").trim();
	        phone.value = String(client.phone || "").trim();

	        roleItems.value = normalizeRoles({
            roles: (staffPayload as any)?.roles_available || [],
          }).map((item) => {
            const available = (staffPayload as any)?.roles_available || [];
            const matched = Array.isArray(available)
              ? available.find((r: any) => String(r?.id) === String(item.id))
              : null;
            return {
              ...item,
              role_name: matched?.role_name ?? null,
            };
          });
	        role.value = getSelectedRoleIds(Array.isArray((s as any).roles) ? (s as any).roles : [], roleItems.value);
        showForm.value = true;
    } catch (err: any) {
        loadStaffError.value = (err && err.message) || "Failed to load staff details";
        // eslint-disable-next-line no-console
        console.error("fetchStaffDetails failed", err);
    } finally {
        isLoadingStaff.value = false;
        rolesLoading.value = false;
        showValidationErrors.value = false;
    }
});

async function handleSubmit() {
  showValidationErrors.value = true;
  if (!validate()) return;
  isSubmitting.value = true;

  try {
    const payload: StaffPayload = {
      ...(staffId.value ? { id: staffId.value } : {}),
      first_name: firstName.value.trim(),
      last_name: lastName.value.trim(),
      email: email.value.trim(),
      phone: phone.value.trim(),
      assigned_roles: role.value,
    };
    await handleSubmitStaff(payload);
    closeForm();
    router.push({ name: "vendor.staffs" }).catch(() => undefined);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Create staff failed", error);
  } finally {
    isSubmitting.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      (isLoadingStaff.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_v_progress_circular, {
                indeterminate: "",
                color: "primary"
              }),
              _cache[4] || (_cache[4] = _createElementVNode("p", { class: "loading-text" }, "Loading staff details...", -1))
            ])
          ]))
        : (_openBlock(), _createBlock(_component_v_form, {
            key: 1,
            onSubmit: _withModifiers(handleSubmit, ["prevent"])
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(AppTextField, {
                    modelValue: firstName.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((firstName).value = $event)),
                      syncValidation
                    ],
                    label: "First Name",
                    "label-icon": "mdi-account-outline",
                    placeholder: "John",
                    "auto-complete": "first_name",
                    "error-messages": 
              showValidationErrors.value && firstNameError.value ? [firstNameError.value] : []
            ,
                    rules: [required]
                  }, null, 8, ["modelValue", "error-messages", "rules"])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(AppTextField, {
                    modelValue: lastName.value,
                    "onUpdate:modelValue": [
                      _cache[1] || (_cache[1] = ($event: any) => ((lastName).value = $event)),
                      syncValidation
                    ],
                    label: "Last Name",
                    "auto-complete": "last_name",
                    "label-icon": "mdi-account-outline",
                    placeholder: "Doe",
                    "error-messages": showValidationErrors.value && lastNameError.value ? [lastNameError.value] : [],
                    rules: [required]
                  }, null, 8, ["modelValue", "error-messages", "rules"])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(AppMobileField, {
                    modelValue: phone.value,
                    "onUpdate:modelValue": [
                      _cache[2] || (_cache[2] = ($event: any) => ((phone).value = $event)),
                      syncValidation
                    ],
                    label: "Phone Number",
                    "label-icon": "mdi-phone-outline",
                    placeholder: "(888) 888 8888",
                    "error-messages": showValidationErrors.value && phoneError.value ? [phoneError.value] : [],
                    rules: [required, phoneRule]
                  }, null, 8, ["modelValue", "error-messages", "rules"])
                ]),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(AppTextField, {
                    modelValue: email.value,
                    "onUpdate:modelValue": [
                      _cache[3] || (_cache[3] = ($event: any) => ((email).value = $event)),
                      syncValidation
                    ],
                    readonly: false,
                    label: "Email",
                    "label-icon": "mdi-email-outline",
                    placeholder: "info@example.com",
                    type: "email",
                    "error-messages": showValidationErrors.value && emailError.value ? [emailError.value] : [],
                    rules: [required, emailRule]
                  }, null, 8, ["modelValue", "error-messages", "rules"])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, [
                    _createVNode(AppFieldLabel, {
                      icon: "mdi-shield-account-outline",
                      label: "Assign Role(s)"
                    })
                  ]),
                  _createElementVNode("div", _hoisted_10, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roleItems.value, (item) => {
                      return (_openBlock(), _createBlock(_component_v_checkbox, {
                        key: String(item.id),
                        label: item.label,
                        disabled: rolesLoading.value,
                        "model-value": role.value.includes(item.id),
                        inline: "",
                        density: "compact",
                        class: "mr-4",
                        "hide-details": "",
                        "onUpdate:modelValue": (checked) => onRoleToggle(item.id, checked)
                      }, null, 8, ["label", "disabled", "model-value", "onUpdate:modelValue"]))
                    }), 128))
                  ]),
                  (showValidationErrors.value && roleError.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(roleError.value), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createVNode(AppButtonFlat, {
                  type: "submit",
                  block: "",
                  loading: isSubmitting.value,
                  "prepend-icon": "mdi-content-save"
                }, {
                  default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                    _createTextVNode(" Submit ", -1)
                  ]))]),
                  _: 1
                }, 8, ["loading"])
              ])
            ]),
            _: 1
          }))
    ]),
    _: 1
  }))
}
}

})